"use client";

import { useCallback, type MutableRefObject, type Dispatch, type SetStateAction } from "react";
import {
  createLobbyConnection,
  type PokerHubConnection,
  type LobbyHubEvents,
  type TableHubEvents,
} from "@/lib/texas-holdem-signalr";
import type {
  RoomDto,
  PokerTableView,
  ChatMessageDto,
  GameLogEntry,
  CreateRoomRequest,
  JoinRoomRequest,
} from "@/lib/texas-holdem-types";
import { getSavedSession, saveSession, clearSession } from "@/lib/poker-session";
import type { ConnectionStatus } from "./use-texas-holdem";

export interface PokerRoomDeps {
  lobbyRef: MutableRefObject<PokerHubConnection<LobbyHubEvents> | null>;
  tableRef: MutableRefObject<PokerHubConnection<TableHubEvents> | null>;
  deviceId: MutableRefObject<string>;
  pendingBlindScheduleRef: MutableRefObject<import("@/lib/texas-holdem-types").BlindScheduleDto | null>;
  setupLobbyHandlers: (lobby: PokerHubConnection<LobbyHubEvents>) => void;
  setRoom: Dispatch<SetStateAction<RoomDto | null>>;
  setTableView: Dispatch<SetStateAction<PokerTableView | null>>;
  setChatMessages: Dispatch<SetStateAction<ChatMessageDto[]>>;
  setGameLog: Dispatch<SetStateAction<GameLogEntry[]>>;
  setError: Dispatch<SetStateAction<string | null>>;
  setConnectionStatus: Dispatch<SetStateAction<ConnectionStatus>>;
  setPlayerId: Dispatch<SetStateAction<string | null>>;
  setPlayerName: Dispatch<SetStateAction<string>>;
  setIsHost: Dispatch<SetStateAction<boolean>>;
  setSavedSessionRoomCode: Dispatch<SetStateAction<string | null>>;
}

export function usePokerRoom(deps: PokerRoomDeps) {
  const {
    lobbyRef, tableRef, deviceId, pendingBlindScheduleRef,
    setupLobbyHandlers,
    setRoom, setTableView, setChatMessages, setGameLog,
    setError, setConnectionStatus, setPlayerId, setPlayerName,
    setIsHost, setSavedSessionRoomCode,
  } = deps;

  const createRoom = useCallback(
    async (name: string, settings: Partial<CreateRoomRequest> = {}) => {
      setError(null);
      setConnectionStatus("connecting");

      if (lobbyRef.current) {
        try { await lobbyRef.current.disconnect(); } catch {}
      }

      const lobby = createLobbyConnection();
      lobbyRef.current = lobby;
      setupLobbyHandlers(lobby);

      try {
        await lobby.connect();
        setConnectionStatus("connected");

        if (settings.blindSchedule) {
          pendingBlindScheduleRef.current = settings.blindSchedule;
        }

        const { blindSchedule: _bs, ...restSettings } = settings;
        const request: CreateRoomRequest = {
          hostName: name,
          variant: restSettings.variant ?? "NlHoldem",
          gameMode: restSettings.gameMode,
          smallBlind: restSettings.smallBlind ?? 10,
          bigBlind: restSettings.bigBlind ?? 20,
          minBuyIn: restSettings.minBuyIn ?? 1000,
          maxBuyIn: restSettings.maxBuyIn ?? 5000,
          maxPlayers: restSettings.maxPlayers ?? 10,
          turnTimerSeconds: restSettings.turnTimerSeconds ?? 30,
          timeBankSeconds: restSettings.timeBankSeconds ?? 60,
          autoDeal: restSettings.autoDeal ?? true,
          isPublic: restSettings.isPublic ?? false,
          deviceId: deviceId.current,
          timestamp: Date.now(),
          avatarUrl: typeof window !== "undefined" ? localStorage.getItem("poker_avatar") || undefined : undefined,
        };

        let result: RoomDto | null = null;
        try {
          result = await lobby.invoke<RoomDto | null>("CreateRoom", request);
        } catch {
          await new Promise((r) => setTimeout(r, 2000));
          result = await lobby.invoke<RoomDto | null>("CreateRoom", {
            ...request,
            timestamp: Date.now(),
          });
        }
        if (!result) return null;

        setRoom(result);
        setPlayerName(name);
        setIsHost(true);

        const hostPlayer = result.players.find((p) => p.isHost);
        if (hostPlayer) {
          setPlayerId(hostPlayer.id);
          saveSession({ roomCode: result.code, playerId: hostPlayer.id, playerName: name });
          setSavedSessionRoomCode(result.code);
        }

        return result;
      } catch {
        setConnectionStatus("disconnected");
        setError("Failed to create room");
        return null;
      }
    },
    [lobbyRef, deviceId, pendingBlindScheduleRef, setupLobbyHandlers, setRoom, setPlayerName, setIsHost, setPlayerId, setSavedSessionRoomCode, setError, setConnectionStatus]
  );

  const joinRoom = useCallback(
    async (code: string, name: string) => {
      setError(null);
      setConnectionStatus("connecting");

      if (lobbyRef.current) {
        try { await lobbyRef.current.disconnect(); } catch {}
      }

      const lobby = createLobbyConnection();
      lobbyRef.current = lobby;
      setupLobbyHandlers(lobby);

      try {
        await lobby.connect();
        setConnectionStatus("connected");

        const request: JoinRoomRequest = {
          roomCode: code.toUpperCase(),
          playerName: name,
          deviceId: deviceId.current,
          timestamp: Date.now(),
          avatarUrl: typeof window !== "undefined" ? localStorage.getItem("poker_avatar") || undefined : undefined,
        };

        let result: RoomDto | null = null;
        try {
          result = await lobby.invoke<RoomDto | null>("JoinRoom", request);
        } catch {
          await new Promise((r) => setTimeout(r, 2000));
          result = await lobby.invoke<RoomDto | null>("JoinRoom", {
            ...request,
            timestamp: Date.now(),
          });
        }
        if (!result) return null;

        setRoom(result);
        setPlayerName(name);

        const me = result.players.findLast((p) => p.name === name) ?? result.players[result.players.length - 1];
        if (me) {
          setPlayerId(me.id);
          setIsHost(me.isHost);
          saveSession({ roomCode: result.code, playerId: me.id, playerName: name });
          setSavedSessionRoomCode(result.code);
        }

        return result;
      } catch {
        setConnectionStatus("disconnected");
        setError("Failed to join room");
        return null;
      }
    },
    [lobbyRef, deviceId, setupLobbyHandlers, setRoom, setPlayerName, setIsHost, setPlayerId, setSavedSessionRoomCode, setError, setConnectionStatus]
  );

  const startGame = useCallback(async () => {
    if (!lobbyRef.current) return false;
    try {
      return await lobbyRef.current.invoke<boolean>("StartGame");
    } catch {
      setError("Failed to start game");
      return false;
    }
  }, [lobbyRef, setError]);

  const kickPlayer = useCallback(async (targetId: string) => {
    if (!lobbyRef.current) return;
    try {
      await lobbyRef.current.invoke("KickPlayer", targetId);
    } catch {
      setError("Failed to kick player");
    }
  }, [lobbyRef, setError]);

  const rejoinLobby = useCallback(
    async (code: string) => {
      const session = getSavedSession();
      if (!session) return null;

      setError(null);
      setConnectionStatus("connecting");

      if (lobbyRef.current) {
        try { await lobbyRef.current.disconnect(); } catch {}
      }

      const lobby = createLobbyConnection();
      lobbyRef.current = lobby;
      setupLobbyHandlers(lobby);

      try {
        await lobby.connect();
        setConnectionStatus("connected");

        const request: JoinRoomRequest = {
          roomCode: code.toUpperCase(),
          playerName: session.playerName,
          deviceId: deviceId.current,
          timestamp: Date.now(),
          avatarUrl: typeof window !== "undefined" ? localStorage.getItem("poker_avatar") || undefined : undefined,
        };

        const result = await lobby.invoke<RoomDto | null>("JoinRoom", request);
        if (!result) {
          setConnectionStatus("disconnected");
          return null;
        }

        setRoom(result);
        setPlayerName(session.playerName);

        const me = result.players.find((p) => p.id === session.playerId)
          ?? result.players.find((p) => p.name === session.playerName)
          ?? result.players[result.players.length - 1];
        if (me) {
          setPlayerId(me.id);
          setIsHost(me.isHost);
          saveSession({ roomCode: result.code, playerId: me.id, playerName: session.playerName });
          setSavedSessionRoomCode(result.code);
        }

        return result;
      } catch {
        setConnectionStatus("disconnected");
        setError("Failed to rejoin room");
        return null;
      }
    },
    [lobbyRef, deviceId, setupLobbyHandlers, setRoom, setPlayerName, setIsHost, setPlayerId, setSavedSessionRoomCode, setError, setConnectionStatus]
  );

  const leaveTable = useCallback(async () => {
    if (tableRef.current) {
      try {
        await tableRef.current.invoke("LeaveTable");
      } catch {}
      try { await tableRef.current.disconnect(); } catch {}
      tableRef.current = null;
    }
    setTableView(null);
    setChatMessages([]);
    setGameLog([]);
  }, [tableRef, setTableView, setChatMessages, setGameLog]);

  const leaveRoom = useCallback(async () => {
    if (lobbyRef.current) {
      try {
        await lobbyRef.current.invoke("LeaveRoom");
      } catch {}
      try { await lobbyRef.current.disconnect(); } catch {}
      lobbyRef.current = null;
    }
    await leaveTable();
    setRoom(null);
    setPlayerId(null);
    setIsHost(false);
    clearSession();
    setSavedSessionRoomCode(null);
    setConnectionStatus("disconnected");
  }, [lobbyRef, leaveTable, setRoom, setPlayerId, setIsHost, setSavedSessionRoomCode, setConnectionStatus]);

  return {
    createRoom,
    joinRoom,
    startGame,
    kickPlayer,
    rejoinLobby,
    leaveTable,
    leaveRoom,
  };
}
